/* 2026-08 — Admin/Editor Loom bridge manuscript picker.
 *
 * Lets a full Admin or an "editor"-scope staff account pick ANY
 * manuscript on the platform and open it straight inside The Loom
 * (the same writing environment authors use) to assess/rewrite it.
 * Mechanics: mints the "Admin Troubleshooting" sandbox writer session
 * (adminApi.loomBridge), which also adds that sandbox account as an
 * "editor" project_members collaborator on the chosen manuscript, then
 * navigates to /app?project=<id> — the Loom's existing deep-link param
 * (see lib/store.js bootstrap) opens straight into it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ChevronsUpDown, Loader2, PenSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { adminApi } from '@/lib/api';

export default function AdminLoomManuscriptPicker() {
  const [open, setOpen] = useState(false);
  const [manuscripts, setManuscripts] = useState(null);
  const [opening, setOpening] = useState(null);

  const load = useCallback(() => {
    adminApi.loomManuscripts().then((d) => setManuscripts(d.manuscripts || [])).catch(() => setManuscripts([]));
  }, []);

  useEffect(() => { if (open && manuscripts === null) load(); }, [open, manuscripts, load]);

  const openManuscript = async (m) => {
    setOpening(m.id);
    try {
      await adminApi.loomBridge(m.id);
      window.location.href = `/app?project=${encodeURIComponent(m.id)}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open this manuscript in The Loom.');
      setOpening(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost" size="sm" className="h-7 gap-1.5 text-[12px]"
          data-testid="admin-hub-open-manuscript-loom"
        >
          <PenSquare className="h-3.5 w-3.5" /> Open Manuscript <ChevronsUpDown className="h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" data-testid="admin-loom-manuscript-picker">
        <Command>
          <CommandInput placeholder="Search manuscripts or authors…" data-testid="admin-loom-manuscript-search" />
          <CommandList>
            {manuscripts === null ? (
              <div className="flex items-center justify-center gap-2 py-6 text-[12px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading manuscripts…
              </div>
            ) : (
              <>
                <CommandEmpty>No manuscripts found.</CommandEmpty>
                <CommandGroup heading={`${manuscripts.length} manuscripts`}>
                  {manuscripts.map((m) => (
                    <CommandItem
                      key={m.id}
                      value={`${m.title} ${m.alt_title || ''} ${m.author_name}`}
                      onSelect={() => openManuscript(m)}
                      disabled={opening === m.id}
                      data-testid={`admin-loom-manuscript-option-${m.id}`}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">{m.title}</span>
                        <span className="truncate text-[11px] text-muted-foreground">
                          {m.author_name}
                          {m.alt_title ? ` · catalogued as “${m.alt_title}”` : ''}
                        </span>
                      </span>
                      {opening === m.id && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
